import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const LOCK_ACCESS_TYPES = [Action.StackLock];

const hasLockAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(LOCK_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can lock a stack.
 * A user needs write access to lock a stack.
 * A user can't lock a stack that is already locked.
 * Stacks created from a template deployment (blueprintDeployment) cannot be locked.
 * @returns {(stack: Stack) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can lock it
 */
const useCanLockStack = (): ((stack: Stack) => AccessResult) => {
  const hasStackWriteAccess = useHasStackWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const isNotLocked = !stack.lockedAt && !stack.lockedBy;

      if (!isNotLocked) {
        return {
          result: false,
        };
      }

      if (hasStackWriteAccess(stack).result) {
        return {
          result: true,
        };
      }

      if (hasLockAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: NO_ACCESS_MESSAGE,
      };
    },
    [hasStackWriteAccess, viewer]
  );
};

export default useCanLockStack;
